import { computed, inject, Injectable } from '@angular/core';

import { filterMenuGroupsByEffectiveRole, menuGroups } from '../config/entity-config';
import { AuthService } from './auth.service';
import { isSystemRoleCode, resolveEffectiveRole, type RoleCodeValue } from '../utils/role-access';

/**
 * Effective role and filtered sidebar menu derived from the authenticated user.
 */
@Injectable({
  providedIn: 'root',
})
export class RoleAccessService {
  private readonly auth = inject(AuthService);

  readonly effectiveRole = computed<RoleCodeValue | null>(() => resolveEffectiveRole(this.auth.user()));

  /** True when the token's active assignment is a system role (create actions, etc.). */
  readonly isActiveSystemRole = computed(
    () => this.auth.user()?.active_assignment?.role_type === 'SYSTEM',
  );

  /**
   * True when the user may see Internal API documentation (staff or any SYSTEM_* role).
   */
  readonly canViewInternalDocs = computed(() => {
    const user = this.auth.user();
    if (!user) {
      return false;
    }
    if (user.is_staff) {
      return true;
    }
    if (user.effective_role_type === 'SYSTEM' || user.active_assignment?.role_type === 'SYSTEM') {
      return true;
    }
    if ((user.role_assignments ?? []).some((assignment) => assignment.role_type === 'SYSTEM')) {
      return true;
    }
    if ((user.active_roles ?? []).some((code) => code.startsWith('SYSTEM_'))) {
      return true;
    }
    return isSystemRoleCode(this.effectiveRole());
  });

  readonly menuGroups = computed(() => filterMenuGroupsByEffectiveRole(menuGroups, this.effectiveRole()));
}
